<template>
  <div class="search__header">
    <h2 v-if="searchedKeyword && !loadingResults" aria-live="polite">{{ resultsCount }} results found for ‘{{ searchedKeyword }}’</h2>
    <h2 v-else-if="keyword">Searching for ‘{{ keyword }}’ ...</h2>
    <h2 v-else>{{ config.labels.title }}</h2>
    <div class="search__keyword">
      <label class="sr-only" for="search-input">Search</label>
      <input
        id="search-input"
        type="search"
        class="search__input"
        v-model="keyword"
        @input="onKeywordChange"
        :placeholder="searchPlaceholder"
      />
      <i class="material-icons text-primary">search</i>
    </div>
    <a
      v-if="config.secondarySearch"
      :href="secondarySearchURL"
      class="search__secondary-link btn btn-outline-primary btn-round"
      >{{ config.secondarySearch.title }}</a
    >

    <div class="search__filters">
      <h3 class="search__hint">{{ config.labels.filtersHint }}</h3>
      <div class="row">
        <div v-for="(filterConfig, i) in config.filters"
          :class="[filterConfig.placeholder === 'Topics' ? 'col-md-12' : 'col-md-6 col-lg-6']"
          :key="filterConfig.name">
          <div class="v-select-holder v-multi-select-holder">
            <v-select
              :key="i"
              v-model="filters[filterConfig.name]"
              :multiple="true"
              :deletable-chips="true"
              @change="onFilterChange"
              @click="onSelectorClicked"
              @blur="onSelectorBlurred"
              :items="filterConfig.options"
              :chips="true"
              item-text="name"
              item-value="value"
              :small-chips="true"
              :menu-props="{ maxHeight: '400' }"
              :label="filterConfig.placeholder"
              ref="filterSelect"
              :prepend-inner-icon="`keyboard_arrow_down`"
            ></v-select>
          </div>
        </div>
        <div class="col-md-6" v-if="config.date">
          <div class="v-select-holder">
            <v-select
              v-model="dateFilter"
              @input="onDateFilterChange"
              @click="onSelectorClicked"
              @blur="onSelectorBlurred"
              :items="config.date.options"
              item-text="name"
              item-value="value"
              :menu-props="{ maxHeight: '400', closeOnContentClick: true }"
              label="By date"
              ref="dateSelector"
              :prepend-inner-icon="`keyboard_arrow_down`"
            ></v-select>
          </div>
          <div v-if="dateFilter && dateFilter == 'range'" class="search__dates row">
            <div class="col-sm-4">
              <label class="search__date search__date-from">
                Date From
                <input type="date" v-model="dateFrom" @input="search" />
              </label>
            </div>
            <div class="col-sm-4">
              <label class="search__date search__date-to">
                Date To
                <input type="date" v-model="dateTo" @input="search" />
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { searchConfig, debounce, buildSearchQueryString } from "@/utils"

export default {
  props: {
    resultsCount: Number,
    loadingResults: Boolean,
  },

  data() {
    return {
      keyword: "",
      searchedKeyword: "",
      filters: {},
      dateFilter: null,
      dateFrom: null,
      dateTo: null,
      config: searchConfig,
    }
  },

  computed: {
    searchParams: function() {
      const filters = {}
      this.config.filters.forEach(filter => {
        if (this.filters[filter.name]) {
          filters[filter.name] = this.filters[filter.name].map(option => option.value ? option.value : option)
        }
      })

      return {
        q: this.keyword,
        sort: this.dateFilter && ["asc", "desc"].includes(this.dateFilter) ? this.dateFilter : "",
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        ...filters,
      }
    },
    url: function() {
      return window.href
    },
    secondarySearchURL: function() {
      if (!this.config.secondarySearch) {
        return ""
      }

      return `${this.config.secondarySearch.url}${buildSearchQueryString(this.searchParams)}`
    },
    searchPlaceholder: function() {
      return this.config.placeholder || ""
    },
  },

  watch: {
    loadingResults: function(isLoading) {
      if (!isLoading) {
        this.searchedKeyword = this.keyword
      }
    },
  },

  created() {
    this.initFilters()
    this.search()

    // update search on back/forward button
    const _this = this
    window.onpopstate = function() {
      _this.initFilters()
      _this.search(false)
    }
  },

  methods: {
    onSelectorBlurred(e) {
      this.$nextTick().then(() => {
        const menu = e.target.parentNode.parentNode
        menu.setAttribute("role", "button")
      })
    },
    onSelectorClicked(e) {
      this.$nextTick().then(() => {
        const menu = e.target.parentNode.parentNode
        menu.removeAttribute("role", "button")
      })
    },
    findSelector(key) {
      if (key.startsWith("filterSelect")) {
        return this.$refs.filterSelect.find(item => item.$el.dataset.key == key)
      }

      return this.$refs.dateSelect
    },
    screenReaderShim(e) {
      let btn = e.target
      while (!btn.classList.contains("multiselect") && btn.parentNode) {
        btn = btn.parentNode
      }

      btn = this.findSelector(btn.dataset.key)

      if (btn) {
        btn.activate()
      }
    },
    keySpaceDown(e, el) {
      e.preventDefault()
      e.stopImmediatePropagation()
      e.stopPropagation()
      const cur = el.$refs.list.children[0].children[el.curPointer].querySelector(".multiselect__option")
      cur.dispatchEvent(new Event("click"))
    },

    keyTabDown(e, el) {
      if (el.isOpen) {
        e.preventDefault()
        e.stopImmediatePropagation()
        e.stopPropagation()

        if (e.shiftKey) {
          const n = el.curPointer
          if (n == 0) {
            el.pointer = el.options.length - 1
            el.$refs.list.scrollTop = el.$refs.list.scrollHeight
          } else {
            el.pointer--
          }

          el.curPointer = el.pointer
        } else {
          const n = el.curPointer
          if (n == el.options.length - 1) {
            el.pointer = 0
            el.$refs.list.scrollTop = 0
          } else {
            el.pointer++
          }

          el.curPointer = el.pointer
        }

        const cur = el.$refs.list.children[0].children[el.curPointer]
        if (el.$refs.list.clientHeight - el.$refs.list.scrollTop <= cur.offsetTop) {
          el.$refs.list.scrollTop = cur.offsetTop
        } else if (cur.offsetTop - el.$refs.list.scrollTop < 0) {
          el.$refs.list.scrollTop = cur.offsetTop
        }
      }
    },
    keyArrowUp(el) {
      const n = el.curPointer
      if (n == 0) {
        el.pointer = el.options.length - 1
        el.$refs.list.scrollTop = el.$refs.list.scrollHeight
      }

      el.curPointer = el.pointer
    },
    keyArrowDown(el) {
      const n = el.curPointer
      if (n == el.options.length - 1) {
        el.pointer = 0
        el.$refs.list.scrollTop = 0
      }

      el.curPointer = el.pointer
    },
    initFilters() {
      const uri = window.location.search.substring(1)
      const params = new URLSearchParams(decodeURI(uri))

      this.searchedKeyword = this.keyword = params.get("q")

      const activeFilters = {}
      this.config.filters.forEach(filter => {
        activeFilters[filter.name] = filter.options.filter(option => {
          return params.getAll(`${filter.name}[]`).includes(option.value)
        })
      })
      this.filters = activeFilters

      if (this.config.date) {
        this.dateFilter = this.config.date.options.find(option => params.get("sort") == option.value)

        if (params.get("dateFrom") || params.get("dateTo")) {
          this.dateFilter = "range"
          this.dateFrom = params.get("dateFrom")
          this.dateTo = params.get("dateTo")

          console.log(this.dateFrom)
          console.log(this.dateTo)
        }
      }
    },

    onKeywordChange() {
      this.debouncedSearch()
    },

    onFilterChange() {
      this.search()
    },

    onDateFilterChange() {
      this.dateFrom = this.dateTo = null

      if (this.dateFilter && this.dateFilter != "range") {
        this.search()
      }

      // just in case I get questioned 3 months later why I placed these lines:
      // it's because vuetify's :menu-props DOES NOT work
      this.$refs.dateSelector.$el.querySelector(".v-menu").style.display = "none"
      this.$refs.dateSelector.blur()
      this.$nextTick().then(() => {
        document.addEventListener("mousedown", this.clickContentToCloseHack)
      })
    },

    clickContentToCloseHack() {
      document.removeEventListener("mousedown", this.clickContentToCloseHack)
      setTimeout(() => {
        this.$refs.dateSelector.$el.querySelector(".v-menu").removeAttribute("style")
      }, 300);
    },

    debouncedSearch: debounce(function() {
      this.search()
    }, 500),

    search(updateURL = true) {
      if (!this.config.allowEmptyKeyword && !this.keyword) {
        return
      }

      if (updateURL) {
        const pagePath = window.location.pathname.replace(/\/$/, "")
        window.history.pushState({}, "", `${pagePath}/${buildSearchQueryString(this.searchParams)}`)
      }

      this.$emit("search", this.searchParams)
    },
  },
}
</script>

<style lang="scss" scoped>
.multiselect {
  margin-bottom: 10px;
}
.search {
  &__header {
    h2 {
      margin-bottom: 18px;
      @media (min-width: 768px) {
        margin-right: 240px;
      }
    }
  }
  &__keyword {
    margin-bottom: 20px;
    font-size: 1rem;
    position: relative;

    .material-icons {
      font-size: 32px;
      position: absolute;
      right: 8px;
      top: calc(50% - 16px);
    }
  }

  &__input {
    padding-right: 48px;
  }

  &__filters {
    margin-bottom: 20px;
  }

  &__date {
    width: 100%;
  }

  &__secondary-link {
    margin-bottom: 20px;

    @media (min-width: 768px) {
      position: absolute;
      right: 0;
      top: 0;
    }
  }
}
</style>
